import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import CreatableSelect from 'react-select/creatable';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const WorkPermitCreation = ({ isOpen, onClose, refreshPreOrders, mode = "create", order = null }) => {
  const [orderData, setOrderData] = useState({
    orderNumber: '',
    workPermitNumber: '',
    turbineId: '',
    scopeOfWorkId: '',
    workScopeReal: '',
    comment: '',
  });
  const { user } = useContext(UserContext);
  const [turbines, setTurbines] = useState([]);
  const [scopesOfWork, setScopesOfWork] = useState([]);
  const [otherScopeId, setOtherScopeId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('No authentication token found');

        const config = {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        };

        const [turbinesResponse, scopesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/turbines/`, config),
          axios.get(`${API_BASE_URL}/scopeofworks/`, config),
        ]);

        const turbinesData = turbinesResponse.data || [];
        const scopesData = scopesResponse.data || [];

        console.log('Pobrane dane z /turbines/:', turbinesData);
        console.log('Pobrane dane z /scopeofworks/:', scopesData);

        setTurbines(turbinesData);
        setScopesOfWork(scopesData);

        const otherScope = scopesData.find(scope => scope.name === 'Other' && scope.company === 1);
        setOtherScopeId(otherScope ? otherScope.id : null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
      if (mode === "edit" && order) {
        setOrderData({
          orderNumber: order.order_number || '',
          workPermitNumber: order.work_permit_number || '',
          turbineId: order.turbine || '',
          scopeOfWorkId: order.work_scope || '',
          workScopeReal: order.work_scope_real || '',
          comment: order.comment || '',
        });
      } else {
        setOrderData({
          orderNumber: '',
          workPermitNumber: '',
          turbineId: '',
          scopeOfWorkId: '',
          workScopeReal: '',
          comment: '',
        });
      }
    }
  }, [isOpen, mode, order]);

  const turbineOptions = turbines
    .filter(turbine => turbine.company === 1)
    .map(turbine => ({
      value: turbine.id,
      label: `${turbine.turbine_number} (${turbine.model_data.name || 'N/A'})`,
    }));

  const scopeOptions = scopesOfWork
  .filter(scope => scope.company === 1 && scope.name !== 'Other')
  .map(scope => ({
    value: scope.id,
    label: scope.name || 'N/A',
  }));

  const handleChange = (field, selectedOption) => {
    if (field === 'scopeOfWorkId') {
      if (selectedOption && selectedOption.__isNew__ ) {
        setOrderData(prev => ({
          ...prev,
          scopeOfWorkId: otherScopeId,
          workScopeReal: selectedOption.__isNew__ ? selectedOption.label : '',
        }));
      } else {
        setOrderData(prev => ({
          ...prev,
          scopeOfWorkId: selectedOption ? selectedOption.value : '',
          workScopeReal: '',
        }));
      }
    } else {
      setOrderData(prev => ({
        ...prev,
        [field]: selectedOption ? selectedOption.value : '',
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setOrderData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!otherScopeId && orderData.workScopeReal) {
      setErrorMessage('Custom scope is not supported because "Other" option is not available.');
      return;
    }

    const submitData = {
      order_number: orderData.orderNumber,
      work_permit_number: orderData.workPermitNumber,
      turbine: orderData.turbineId,
      work_scope: orderData.scopeOfWorkId,
      work_scope_real: orderData.workScopeReal || null,
      comment: orderData.comment,
      company_to: 1,
      company: user?.company,
    };

    if (mode === "create") {
      submitData.status = 'pending';
    }

    console.log(`Dane wysyłane do /workpermit/${mode === "edit" ? order.id : ''}:`, submitData);

    const requiredFields = ['order_number', 'turbine', 'work_scope'];
    const missingFields = requiredFields.filter(field => !submitData[field]);
    if (missingFields.length > 0) {
      setErrorMessage(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    if (submitData.work_scope === otherScopeId && !submitData.work_scope_real) {
      setErrorMessage('Please provide a description for the custom scope of work.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setErrorMessage('No authentication token found. Please log in.');
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      let response;
      if (mode === "create") {
        response = await axios.post(`${API_BASE_URL}/workpermit/`, submitData, config);
      } else {
        response = await axios.put(`${API_BASE_URL}/workpermit/${order.id}/`, submitData, config);
      }

      console.log('API Response:', response.data);
      refreshPreOrders();
      onClose();
    } catch (error) {
      console.error(`Error ${mode === "create" ? "creating" : "updating"} work permit:`, error);
      if (error.response?.data?.non_field_errors) {
        const nonFieldError = error.response.data.non_field_errors[0];
        if (nonFieldError.includes('work_permit_number, company must make a unique set')) {
          setErrorMessage('Work permit number already exists for your company.');
        } else if (nonFieldError.includes('order_number, company must make a unique set')) {
          setErrorMessage('Order number already exists for your company.');
        } else {
          setErrorMessage(nonFieldError || `Error ${mode === "create" ? "creating" : "updating"} work permit. Please try again.`);
        }
      } else {
        setErrorMessage(error.response?.data?.detail || `Error ${mode === "create" ? "creating" : "updating"} work permit. Please try again.`);
      }
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <p>Loading...</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit Work Order (Vestas)" : "Create New Work Order (Vestas)"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Order Number</label>
              <input
                type="text"
                value={orderData.orderNumber}
                onChange={e => handleInputChange('orderNumber', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter order number"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Work Permit Number</label>
              <input
                type="text"
                value={orderData.workPermitNumber}
                onChange={e => handleInputChange('workPermitNumber', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter work permit number"
                
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Turbine</label>
              <CreatableSelect
                options={turbineOptions}
                value={turbineOptions.find(option => option.value === orderData.turbineId) || null}
                onChange={option => handleChange('turbineId', option)}
                placeholder="Select or type to filter turbines..."
                isClearable
                className="basic-single"
                classNamePrefix="select"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Scope of Work</label>
              <CreatableSelect
                options={scopeOptions}
                value={
                  orderData.scopeOfWorkId === otherScopeId
                    ? { value: otherScopeId, label: orderData.workScopeReal || 'Other' }
                    : scopeOptions.find(option => option.value === orderData.scopeOfWorkId) || null
                }
                onChange={option => handleChange('scopeOfWorkId', option)}
                placeholder="Select or type a custom scope..."
                isClearable
                isDisabled={!otherScopeId}
                className="basic-single"
                classNamePrefix="select"
                formatCreateLabel={inputValue => `Use custom scope: "${inputValue}"`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={orderData.comment}
                onChange={e => handleInputChange('comment', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter description"
                rows="4"
              />
            </div>
          </div>

          {errorMessage && <div className="text-red-600 text-sm">{errorMessage}</div>}

          <DialogFooter>
            <Button
              variant="outline"
              className="bg-white px-4 py-2 rounded-full hover:bg-gray-100 transition-colors duration-300 flex items-center"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="bg-white px-4 py-2 rounded-full hover:bg-gray-100 transition-colors duration-300 flex items-center"
              type="submit"
            >
              {mode === "edit" ? "Update Work Order" : "Create Work Order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkPermitCreation;